import { Link } from 'react-router-dom'
import ProfileLink from '../UI/ProfileLink'
import { Calendar, MapPin, FileText, Eye, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { getTalentTypeLabel, getTalentTypeColor } from '../../utils/talentTypeUtils'
import { getStatusStyles } from '../../utils/colorUtils'
import { getToolIcon } from '../../utils/toolIconUtils.jsx'

const RecentJobApplications = ({ jobApplications = [], loading = false }) => {
  if (loading) {
    return (
      <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 animate-pulse sm:p-6">
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="h-4 w-32 max-w-full bg-gray-200 dark:bg-gray-700 rounded sm:w-40" />
                    <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
              </div>
              <div className="h-10 w-20 self-end bg-gray-200 dark:bg-gray-700 rounded-md sm:h-8 sm:w-16" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!jobApplications || jobApplications.length === 0) {
    return (
      <div className="p-6 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
        <FileText className="h-12 w-12 mx-auto mb-3 text-light-text-tertiary dark:text-dark-text-tertiary" />
        <p className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">No data</p>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No job applications have been added to the system yet</p>
      </div>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified'

    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'

    // If status has color, use utility to create styles
    if (status.color) {
      const styles = getStatusStyles(status.color, {
        backgroundColorOpacity: 0.1,
        borderOpacity: 0.3
      })
      return styles
    }

    // Default gray color for dark theme support
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }

  const getStatusLabel = (status) => {
    return status?.name || 'Not defined'
  }

  // Функция копирования контакта в буфер обмена
  const copyContactToClipboard = async (contactValue) => {
    try {
      await navigator.clipboard.writeText(contactValue);
      toast.success(`Copied: ${contactValue}`, {
        duration: 2000,
        position: 'bottom-right',
      });
    } catch (err) {
      console.error('Failed to copy contact:', err);
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = contactValue;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(`Copied: ${contactValue}`, {
          duration: 2000,
          position: 'bottom-right',
        });
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        toast.error('Failed to copy contact', {
          duration: 2000,
          position: 'bottom-right',
        });
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
      {jobApplications.map((app) => (
        <div key={app.id} className="p-4 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors sm:p-6">
          <div className="flex min-w-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
              </div>

              {/* Job Application Information */}
              <div className="flex-1 min-w-0">
                <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                  <h4 className="min-w-0 break-words text-sm font-medium text-light-text-primary dark:text-dark-text-primary sm:truncate">
                    <ProfileLink to={`/job-applications/${app.id}`} ariaLabel={`View application of ${app.name || 'No name'}`}>
                      {app.name || 'No name'}
                    </ProfileLink>
                  </h4>
                  
                  {/* Статус заявки */}
                  {app.status && (
                    <span 
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        typeof getStatusColor(app.status) === 'string' 
                          ? getStatusColor(app.status) 
                          : ''
                      }`}
                      style={typeof getStatusColor(app.status) === 'object' 
                        ? getStatusColor(app.status) 
                        : undefined
                      }
                    >
                      {getStatusLabel(app.status)}
                    </span>
                  )}
                  
                  {/* Конвертирован в талант */}
                  {app.talent && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Converted
                    </span>
                  )}
                </div>

                {/* Additional Information */}
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  {app.contacts && app.contacts.length > 0 ? (
                    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                      {app.contacts.slice(0, 2).map((contact, index) => {
                        const contactValue = typeof contact.value === 'object' ? contact.value?.value || 'Not specified' : contact.value || 'Not specified';

                        return (
                          <div key={contact.id} className="flex min-w-0 max-w-full items-center gap-2">
                            {contact.tool && (
                              <span
                                className="inline-flex items-center cursor-help"
                                title={contact.tool.name}
                              >
                                {getToolIcon(contact.tool, { size: 'h-4 w-4' })}
                              </span>
                            )}
                            <div className="flex-1 min-w-0 flex items-center space-x-1">
                              <button
                                type="button"
                                className="min-h-10 max-w-40 truncate text-left text-sm text-light-text-primary dark:text-dark-text-primary cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors sm:min-h-0 sm:max-w-32"
                                onClick={() => copyContactToClipboard(contactValue)}
                                title={`Click to copy: ${contactValue}`}
                              >
                                {contactValue}
                              </button>
                              {contact.is_primary && (
                                <span className="ml-1 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded flex-shrink-0">
                                  Primary
                                </span>
                              )}
                              {contact.is_verified && (
                                <div className="text-green-500 dark:text-green-400 text-xs flex-shrink-0">✓</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {app.contacts.length > 2 && (
                        <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                          +{app.contacts.length - 2} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No contacts</span>
                  )}
                  
                  {app.created_at && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>Added {formatDate(app.created_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 sm:shrink-0">
              <Link
                to={`/job-applications/${app.id}`}
                className="inline-flex min-h-10 items-center px-3 py-2 border border-light-border-secondary dark:border-dark-border-secondary shadow-sm text-sm leading-4 font-medium rounded-md text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Link>
            </div>
          </div>
        </div>
      ))}
      
      {/* Link to all job applications */}
      {jobApplications.length > 0 && (
        <div className="p-6 bg-light-bg-secondary dark:bg-dark-bg-secondary">
          <Link
            to="/job-applications"
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 flex items-center justify-center transition-colors"
          >
            View all job applications
            <span className="ml-1">→</span>
          </Link>
        </div>
      )}
    </div>
  )
}

export default RecentJobApplications
